import { useEffect, useState } from 'react'
import { useEmail } from '../hooks/useEmail'
import EmailList from '../components/EmailList'
import EmailContent from '../components/EmailContent'
import Footer from '../components/Footer'
import mailDerpLogo from '../../logos/mailderp_logo_transparent.svg'
import { Email } from '../types/email'
import CurrentMailbox from '../components/CurrentMailbox'
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  IconButton,
  LinearProgress 
} from '@mui/material'
import { styled } from '@mui/material/styles'
import InfoIcon from '@mui/icons-material/Info'
import CloseIcon from '@mui/icons-material/Close'

// Logo container styled to match original CSS
const LogoContainer = styled(Box)(({  }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  transition: 'height 0.5s ease-out',
  '&.animation-complete': {
    height: '150px',
    position: 'relative',
  }
}));

const StyledLogo = styled('img')({
  maxWidth: '300px',
  height: 'auto',
  transform: 'translateY(0)',
  transition: 'transform 1.8s ease-out',
  paddingTop: '20px',
  '$animation-complete &': {
    maxWidth: '200px',
  }
});

const ContentArea = styled(Box)({
  opacity: 0,
  transition: 'opacity 1s ease-out',
  '&.animation-complete': {
    opacity: 1,
  }
});

// Root container styled component
const RootContainer = styled(Box)({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: 0,
});

// Info blurb styled component
const InfoBlurb = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

function Home() {
  const { 
    currentInbox, 
    loading, 
    emails,
    selectedEmail,
    fetchEmails,
    selectEmail,
    pollingEnabled // Use the polling state from useEmail
  } = useEmail()

  const [animationComplete, setAnimationComplete] = useState(false);
  const [showInfoBlurb, setShowInfoBlurb] = useState(true);
  const [fetchingEmails, setFetchingEmails] = useState(false);
  
  // Track when new email fetch happens via polling
  useEffect(() => {
    if (pollingEnabled && currentInbox) {
      setFetchingEmails(true);
      const timer = setTimeout(() => setFetchingEmails(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [emails, pollingEnabled]);

  // Load emails when current inbox changes
  useEffect(() => {
    if (currentInbox) {
      fetchEmails(currentInbox.id)
    }
  }, [currentInbox, fetchEmails])

  // Handle animation completion
  useEffect(() => {
    const timer = setTimeout(() => setAnimationComplete(true), 1800);
    return () => clearTimeout(timer);
  }, []);

  const handleSelectEmail = (email: Email) => {
    selectEmail(email)
  }

  return (
    <RootContainer>
      <LogoContainer
        className={animationComplete ? 'animation-complete' : ''} 
        sx={{
          mt: animationComplete ? '2vh' : 0,
          mb: animationComplete ? '1vh' : 0,
        }}
      >
        <StyledLogo 
          src={mailDerpLogo} 
          alt="MailDerp Logo"
        />
      </LogoContainer>
      
      <ContentArea className={animationComplete ? 'animation-complete' : ''}>
        {loading && emails.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '50vh' 
          }}>
            <CircularProgress color="primary" size={40} />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Loading...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 3,
          }}>
            {/* Info blurb about temporary emails - now with conditional rendering */}
            {showInfoBlurb && (
              <InfoBlurb elevation={0} sx={{ bgcolor: 'info.main', color: 'info.contrastText', mt: 1 }}>
                <InfoIcon color="inherit" sx={{ mt: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">About Temporary Emails</Typography>
                  <Typography variant="body2">
                    Temporary emails are perfect for signing up to services without revealing your personal email address. 
                    They help protect your privacy and reduce spam in your main inbox.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                    Note: All emails received here will be automatically deleted after 1 hour. Your inbox will auto refresh every 30 seconds.
                  </Typography>
                </Box>
                <IconButton 
                  size="small" 
                  onClick={() => setShowInfoBlurb(false)}
                  sx={{ 
                    color: 'info.contrastText', 
                    opacity: 0.7,
                    '&:hover': { opacity: 1 }
                  }}
                  aria-label="Close info"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InfoBlurb>
            )}

            <Box sx={{ width: '100%' }}>
              <CurrentMailbox />
            </Box>

            {/* Email fetching indicator */}
            {fetchingEmails && (
              <Box sx={{ width: '100%', position: 'relative' }}>
                <LinearProgress 
                  sx={{ 
                    height: 2, 
                    opacity: 0.8,
                    backgroundColor: 'rgba(100, 108, 255, 0.1)'
                  }} 
                />
              </Box>
            )}
            
            <Box sx={{ 
              flex: 1, 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <EmailList 
                onSelectEmail={handleSelectEmail}
                currentInboxEmail={currentInbox?.email} 
              />
              
              {selectedEmail && (
                <EmailContent 
                  email={selectedEmail} 
                  onClose={() => selectEmail(null)}
                />
              )}
            </Box>
          </Box>
        )}
        
        {/* Add the Footer component */}
        <Footer />
      </ContentArea>
    </RootContainer>
  )
}

export default Home